import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/axiosInstance';
import { 
    Search, 
    Filter, 
    ChevronRight, 
    Activity, 
    Clock, 
    User, 
    ArrowLeft, 
    RefreshCcw,
    AlertCircle,
    CheckCircle2,
    XCircle
} from 'lucide-react';
import ExecutionDetail from '../../components/ExecutionDetail';
import { formatTimestamp, formatDuration, compareDates } from '../../utils/dateUtils';
import './styles.scss';

const Executions = () => {
    const [executions, setExecutions] = useState([]);
    const [totalExecutions, setTotalExecutions] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedExecution, setSelectedExecution] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');

    const fetchExecutions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/api/advising/executions');
            // The API now returns { executions: [], total: X, limit: Y }
            const data = response.data.executions || [];
            const total = response.data.total || 0;
            
            // Sort by executedAt desc
            const sorted = data.sort((a, b) => compareDates(a.executedAt, b.executedAt));
            setExecutions(sorted);
            setTotalExecutions(total);
        } catch (err) {
            console.error('Failed to fetch executions:', err);
            setError('Failed to load workflow history. Please check your permissions or try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExecutions();
    }, []);

    const filteredExecutions = executions.filter(exec => {
        const matchesSearch = 
            exec.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exec.executedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exec.agentExecutionId?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === 'ALL' || exec.status === filterStatus;
        
        return matchesSearch && matchesStatus;
    });

    const getStatusIcon = (status) => {
        switch (status) {
            case 'SUCCESS': return <CheckCircle2 className="status-icon completed" size={16} />;
            case 'FAILED': return <XCircle className="status-icon failed" size={16} />;
            case 'CANCELLED': return <AlertCircle className="status-icon cancelled" size={16} />;
            default: return <Activity className="status-icon active" size={16} />;
        }
    };


    if (selectedExecution) {
        return (
            <div className="executions-detail-page">
                <header className="page-header">
                    <button className="back-btn" onClick={() => setSelectedExecution(null)}>
                        <ArrowLeft size={20} />
                        <span>Return to Monitor</span>
                    </button>
                    <div className="header-info">
                        <div className="detail-label">EXECUTION AUDIT LOG</div>
                        <h1>Execution Details</h1>
                        <span className="execution-id">{selectedExecution.agentExecutionId}</span>
                    </div>
                </header>
                <main className="detail-content">
                    <ExecutionDetail executionData={selectedExecution} />
                </main>
            </div>
        );
    }

    return (
        <div className="executions-page-container dashboard-container">
            <header className="page-header-premium">
                <div className="hero-section">
                    <Activity className="header-icon-large" size={64} />
                    <div className="title-stack">

                        <h1 className="dashboard-title">
                            Workflow History <br />
                            <span className="text-gradient">Execution Monitoring</span>
                            <span className="total-badge">{totalExecutions} total</span>
                        </h1>
                        <p className="subtitle">
                            Audit high-fidelity AI diagnostic runs, monitor agent performance, and analyze execution telemetry across the Linqra network.
                        </p>
                    </div>
                </div>
                <div className="refresh-action">
                    <button className="refresh-btn" onClick={fetchExecutions} disabled={loading}>
                        <RefreshCcw size={18} className={loading ? 'spinning' : ''} />
                        <span>Refresh History</span>
                    </button>
                </div>
            </header>

            <div className="controls-bar">
                <div className="search-wrapper">
                    <Search size={18} />
                    <input 
                        type="text" 
                        placeholder="Search by agent, user or ID..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-wrapper">
                    <Filter size={18} />
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="ALL">All Statuses</option>
                        <option value="SUCCESS">Success</option>
                        <option value="FAILED">Failed</option>
                        <option value="CANCELLED">Cancelled</option>
                        <option value="IN_PROGRESS">In Progress</option>
                    </select>
                </div>
            </div>

            {error && (
                <div className="error-state">
                    <AlertCircle size={40} />
                    <p>{error}</p>
                    <button onClick={fetchExecutions}>Try Again</button>
                </div>
            )}

            {loading && !executions.length ? (
                <div className="loading-state">
                    <div className="loader"></div>
                    <p>Fetching executions from Linqra...</p>
                </div>
            ) : (
                <div className="executions-table-wrapper">
                    <table className="executions-table">
                        <thead>
                            <tr>
                                <th>Agent Task</th>
                                <th>Institution</th>
                                <th>Executed By</th>
                                <th>Status</th>
                                <th>Duration</th>
                                <th>Executed At</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredExecutions.map(exec => (
                                <tr key={exec._id?.$oid || exec.agentExecutionId} onClick={() => setSelectedExecution(exec)}>
                                    <td className="agent-col">
                                        <div className="agent-info">
                                            <span className="agent-name">
                                                {((exec.agentTaskName && exec.agentTaskName !== 'N/A') ? exec.agentTaskName : null) || 
                                                 ((exec.taskName && exec.taskName !== 'N/A') ? exec.taskName : null) || 
                                                 exec.agentName || 
                                                 exec.request?.query?.intent || 
                                                 'Workflow Execution'}
                                            </span>
                                            <span className="task-id">
                                                ID: {exec.agentExecutionId?.slice(0, 8) || exec.id?.slice(0, 8)}...
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="institution-info">
                                            <span className="institution-badge">
                                                {exec.institution || 
                                                 exec.resolvedInstitution ||
                                                 exec.request?.institution || 
                                                 exec.request?.query?.params?.institution || 
                                                 exec.inputData?.institution || 
                                                 'N/A'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="user-info">
                                            <User size={14} />
                                            <span>{exec.executedBy || 'System'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={`status-badge ${exec.status?.toLowerCase()}`}>
                                            {getStatusIcon(exec.status)}
                                            <span>{exec.status}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="duration-info">
                                            <Clock size={14} />
                                            <span>{formatDuration(exec.durationMs)}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="timestamp">
                                            {formatTimestamp(exec.executedAt, { 
                                                month: 'short', 
                                                day: 'numeric', 
                                                hour: '2-digit', 
                                                minute: '2-digit' 
                                            })}
                                        </span>
                                    </td>
                                    <td className="actions-col">
                                        <ChevronRight size={18} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!filteredExecutions.length && (
                        <div className="empty-state">
                            <Activity size={40} />
                            <p>No executions found matching your criteria</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Executions;
